import { API } from "aws-amplify";
import Vue from "vue";
import { bodyWithAuthHeader, handleHttpError } from "../utils/utils";

const locService = Vue.prototype.$localizationService;

export default {
  name: "exportModule",
  namespaced: true,

  state: {
    exports: [],
    exportsLoading: false,
    totalExports: 0,
    currentPage: 0,
    pageSize: 20,
    requestingExport: false,
  },

  mutations: {
    setExports(state, {
      items, total, page, size,
    }) {
      state.exports = items;
      state.totalExports = total;
      state.currentPage = page;
      state.pageSize = size;
    },

    setExportsLoading(state, loading) {
      state.exportsLoading = loading;
    },

    setRequestingExport(state, requesting) {
      state.requestingExport = requesting;
    },

    addExport(state, exportItem) {
      state.exports.unshift(exportItem);
      state.totalExports++;
    },

    updateExport(state, updatedExport) {
      const index = state.exports.findIndex((exp) => exp.id === updatedExport.id);
      if (index !== -1) {
        Vue.set(state.exports, index, updatedExport);
      }
    },

    removeExport(state, exportId) {
      const index = state.exports.findIndex((exp) => exp.id === exportId);
      if (index !== -1) {
        state.exports.splice(index, 1);
        state.totalExports--;
      }
    },
  },

  actions: {
    async loadExports({ commit }, { page = 0, size = 20 } = {}) {
      commit("setExportsLoading", true);

      try {
        const body = await bodyWithAuthHeader();
        const response = await API.get("core", `/exports?page=${page}&size=${size}`, body);

        commit("setExports", {
          items: response.items || [],
          total: response.total || 0,
          page: response.page || 0,
          size: response.size || 20,
        });
      } catch (e) {
        handleHttpError(e);
      } finally {
        commit("setExportsLoading", false);
      }
    },

    async requestExport({ commit }, { exportType }) {
      commit("setRequestingExport", true);

      try {
        const body = {
          ...await bodyWithAuthHeader(),
          body: {
            exportType,
          },
        };

        const response = await API.post("core", "/exports", body);
        commit("addExport", response);

        Vue.toasted.success(
          locService.localize("export.request_success"),
          { duration: 5000 },
        );

        return response;
      } catch (e) {
        handleHttpError(e);
        throw e;
      } finally {
        commit("setRequestingExport", false);
      }
    },

    async getExportStatus({ commit }, exportId) {
      try {
        const body = await bodyWithAuthHeader();
        const response = await API.get("core", `/exports/${exportId}`, body);
        commit("updateExport", response);
        return response;
      } catch (e) {
        handleHttpError(e);
        throw e;
      }
    },

    async getDownloadUrl(context, exportId) {
      try {
        const body = await bodyWithAuthHeader();
        const response = await API.get("core", `/exports/${exportId}/download`, body);
        return response.url;
      } catch (e) {
        handleHttpError(e);
        throw e;
      }
    },

    async deleteExport({ commit }, exportId) {
      try {
        const body = await bodyWithAuthHeader();
        await API.del("core", `/exports/${exportId}`, body);
        commit("removeExport", exportId);

        Vue.toasted.success(
          locService.localize("export.delete_success"),
          { duration: 5000 },
        );
      } catch (e) {
        handleHttpError(e);
        throw e;
      }
    },
  },

  getters: {
    getExportById: (state) => (id) => state.exports.find((exp) => exp.id === id),
  },
};
